var themeVars = require("app/theme/variables.js"),
    vars,
    interpolate;

module.exports = vars = {

    /**
     * The breakpoint that changes the layout from mobile layout to medium
     * layout.
     *
     * @returns  {number}  The min-width in pixels
     */
    getMediumLayoutBreakpointPx: function() {
        return 768;
    },

    /**
     * The breakpoint that changes the layout from medium layout to large
     * layout.
     *
     * @returns  {number}  The min-width in pixels
     */
    getLargeLayoutBreakpointPx: function() {
        return 960;
    },

    /**
     * The padding above the cover.
     *
     * @returns  {number}  The padding in pixels
     */
    getCoverAreaPaddingTopPx: function() {
        var paddingTop;

        // Mobile Layout
        if (themeVars.windowWidth < vars.getMediumLayoutBreakpointPx()) {
            paddingTop = interpolate(16, 320, 24, 768);
        }

        // Medium Layout
        else if (themeVars.windowWidth >= vars.getMediumLayoutBreakpointPx()) {
            paddingTop = interpolate(24, 768, 32, 960);
        }

        // Large Layout
        else if (themeVars.windowWidth >= vars.getLargeLayoutBreakpointPx()) {
            paddingTop = interpolate(32, 960, 48, 1920);
        }

        return paddingTop;
    },

    /**
     * The padding below the cover.
     *
     * @returns  {number}  The padding in pixels
     */
    getCoverAreaPaddingBottomPx: function() {
        var paddingBottom;

        // Mobile Layout
        if (themeVars.windowWidth < vars.getMediumLayoutBreakpointPx()) {
            paddingBottom = interpolate(16, 320, 32, 768);
        }

        // Medium Layout
        else if (themeVars.windowWidth >= vars.getMediumLayoutBreakpointPx()) {
            paddingBottom = interpolate(32, 768, 40, 960);
        }

        // Large Layout
        else if (themeVars.windowWidth >= vars.getLargeLayoutBreakpointPx()) {
            paddingBottom = interpolate(32, 960, 48, 1920);
        }

        return paddingBottom;
    },

    /**
     * The padding to the left of the cover.
     *
     * @returns  {number}  The padding in pixels
     */
    getCoverAreaPaddingLeftPx: function() {
        var paddingLeft;

        if (themeVars.windowWidth < 960) {
            paddingLeft = themeVars.safeAreaInsetLeft + themeVars.getPageMarginLargePx();
        }
        else {
            paddingLeft = Math.max(
                themeVars.safeAreaInsetLeft,
                themeVars.getPageMarginLargePx()
            );
        }

        return paddingLeft;
    },

    /**
     * The padding to the right of the cover.
     *
     * @returns  {number}  The padding in pixels
     */
    getCoverAreaPaddingRightPx: function() {
        return vars.getCoverAreaPaddingLeftPx();
    },

    /**
     * The max width of the cover.
     *
     * @param    {Object}  metrics  The layout metrics so far
     * @returns  {number}  The padding in pixels
     */
    getCoverMaxWidthPx: function(metrics) {
        var maxWidth;

        // Mobile & Medium Layouts
        if (themeVars.windowWidth < vars.getLargeLayoutBreakpointPx()) {
            maxWidth = 640;
        }

        // Large Layout
        else if (themeVars.windowWidth >= vars.getLargeLayoutBreakpointPx()) {
            maxWidth = metrics.containerWidth * 0.382;
        }

        // Limit based on containerWidth
        maxWidth = Math.min(
            metrics.containerWidth
                - vars.getCoverAreaPaddingLeftPx()
                - vars.getCoverAreaPaddingRightPx(),
            maxWidth
        );

        return maxWidth;
    },

    /**
     * The max height of the cover.
     *
     * @param    {Object}  metrics  The layout metrics so far
     * @returns  {number}  The padding in pixels
     */
    getCoverMaxHeightPx: function(metrics) {
        var maxHeight;

        // Mobile & Medium Layouts
        if (themeVars.windowWidth < vars.getLargeLayoutBreakpointPx()) {
            maxHeight = metrics.contentRegionHeight * 0.382;
        }

        // Large Layout
        if (themeVars.windowWidth >= vars.getLargeLayoutBreakpointPx()) {
            maxHeight = metrics.containerMinHeight * 0.618;
        }

        maxHeight = Math.max(240, maxHeight);

        return maxHeight;
    },

    /**
     * The popup width at which we start showing a large bookmark button.
     *
     * @returns  {number}  The maximum height in pixels
     */
    getLargeBookmarkButtonMinSpacePx: function() {
        return 280;
    },

    /**
     * Whether to show captions on small action buttons.
     *
     * @returns {boolean} Whether to show captions
     */
    showSmallButtonCaptions: function() {
        // Mobile Layout
        if (themeVars.windowWidth < vars.getMediumLayoutBreakpointPx()) {
            return false;
        }

        // Medium & Large Layouts
        else {
            return true;
        }
    },

    /**
     * The aspect ratio of the background.
     *
     * @param    {Object}  metrics  The layout metrics so far
     * @returns  {number}  The maximum height in pixels
     */
    getBackgroundHeightPx: function(metrics) {
        var height;

        // Mobile & Medium Layouts
        if (themeVars.windowWidth < vars.getLargeLayoutBreakpointPx()) {
            height = (
                metrics.coverHeight
                + metrics.coverAreaPaddingTop
                + metrics.coverAreaPaddingBottom
            ) * 1.618;
        }

        // Large Layout
        else if (themeVars.windowWidth >= vars.getLargeLayoutBreakpointPx()) {
            height = metrics.containerMinHeight;
        }

        return height;
    }
};

/**
 * Interpolates a value linearly between an output range based on a given input
 * range. Ensures that the current value is clamped within the input range to
 * avoid extrapolation.
 *
 * @param    {number}  outputMin        The minimum output value
 * @param    {number}  viewportMin      The minimum viewport width
 * @param    {number}  outputMax        The maximum output value
 * @param    {number}  viewportMax      The maximum viewport width
 * @returns  {number}  The interpolated value
 */
interpolate = function(outputMin, viewportMin, outputMax, viewportMax) {
    var currentViewport,
        clampedViewport,
        out;

    currentViewport = themeVars.windowWidth;

    clampedViewport = Math.min(
        Math.max(currentViewport, viewportMin),
        viewportMax
    );

    out = outputMin
        + (clampedViewport - viewportMin)
        * (outputMax - outputMin)
        / (viewportMax - viewportMin);

    return out;
};
